/**
 * KAN-976: кошелёк кабинета: остаток, журнал с фильтрами и пагинацией,
 * запуск пополнения (после ответа браузер уходит на страницу оплаты ЮKassa).
 */
import { useCallback, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { walletApi } from '@/services/api/wallet';
import { WALLET_QUERY_KEY } from '@/services/api/queryKeys';
import type {
  Wallet,
  WalletEntriesPage,
  WalletEntriesParams,
  WalletEntryKind,
  WalletTopupResponse,
} from '@/types/wallet';

export function useWallet() {
  return useQuery<Wallet>({
    queryKey: WALLET_QUERY_KEY,
    queryFn: walletApi.getWallet,
    staleTime: 30_000,
  });
}

export function useWalletEntries(params: WalletEntriesParams) {
  return useQuery<WalletEntriesPage>({
    queryKey: [...WALLET_QUERY_KEY, 'entries', params],
    queryFn: () => walletApi.getWalletEntries(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Переход на оплату: отдельной функцией, чтобы тест мог её подменить. */
export function goToConfirmation(url: string): void {
  window.location.assign(url);
}

export function useStartTopup() {
  return useMutation<WalletTopupResponse, unknown, string>({
    mutationFn: (amount: string) => walletApi.startTopup(amount),
    onSuccess: (data) => goToConfirmation(data.confirmation_url),
  });
}

/** Фильтры журнала: смена фильтра возвращает на первую страницу. */
export function useWalletEntriesFilters() {
  const [kind, setKindState] = useState<'' | WalletEntryKind>('');
  const [account, setAccountState] = useState('');
  const [page, setPage] = useState(1);
  const debouncedAccount = useDebounce(account.trim(), 400);

  const setKind = useCallback((value: '' | WalletEntryKind) => {
    setKindState(value);
    setPage(1);
  }, []);
  const setAccount = useCallback((value: string) => {
    setAccountState(value);
    setPage(1);
  }, []);

  const params: WalletEntriesParams = { page };
  if (kind) params.kind = kind;
  if (debouncedAccount) params.telegram_account_id = debouncedAccount;

  return { kind, setKind, account, setAccount, page, setPage, params };
}
